import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCustomDateFilterValue,
  selectDateFilterValue,
  selectGeneralStats,
  selectGraphResults,
  selectGraphSpinner,
  selectLimitFilterValue,
  selectLimitStats,
  selectMiningErrorPokersiteId,
  selectMonthStats,
  selectPlayerToShowStats,
  selectShowMiningError,
  selectTableSizeFilterValue,
  selectTableSizeStats,
} from '../../containers/StatPage/selectors';
import {
  changeDateFilter,
  changeLimitFilter,
  changeTableSizeFilter,
  dropFilters,
  findStats,
} from '../../containers/StatPage/actions';
import type { CustomDateFilterValue } from '../../components/DateFilter/DateFilter';
import { TableSizeOptions } from '../../components/TableSizeFilter/constants';
import { LimitOptions } from '../../components/LimitFilter/constants';
import { DateOptions } from '../../components/DateFilter/constants';
import {
  countActiveFilters,
  getDateLabel,
  getLimitLabel,
  getTableSizeLabel,
} from '../utils/filterLabels';

export default function useV2Stats() {
  const dispatch = useDispatch();

  const player = useSelector(selectPlayerToShowStats);
  const generalStats = useSelector(selectGeneralStats) ?? {};
  const graphResults = useSelector(selectGraphResults);
  const graphLoading = useSelector(selectGraphSpinner);
  const monthStats = useSelector(selectMonthStats);
  const tableSizeStats = useSelector(selectTableSizeStats);
  const limitStats = useSelector(selectLimitStats);
  const showMiningError = useSelector(selectShowMiningError);
  const miningErrorPokersiteId = useSelector(selectMiningErrorPokersiteId);

  const tableSizeFilterValue = useSelector(selectTableSizeFilterValue);
  const limitFilterValue = useSelector(selectLimitFilterValue);
  const dateFilterValue = useSelector(selectDateFilterValue);
  const customDateFilterValue = useSelector(selectCustomDateFilterValue);

  const filterSummary = [
    getTableSizeLabel(tableSizeFilterValue),
    getLimitLabel(limitFilterValue),
    getDateLabel(dateFilterValue, customDateFilterValue),
  ];

  const activeFilterCount = countActiveFilters(
    tableSizeFilterValue,
    limitFilterValue,
    dateFilterValue,
    customDateFilterValue,
  );

  const applyFilters = useCallback(
    (draft: {
      tableSizeId?: number;
      limitId: number;
      dateId?: number;
      customDate?: CustomDateFilterValue;
    }) => {
      if (!player) {
        return;
      }

      dispatch(changeTableSizeFilter(draft.tableSizeId));
      dispatch(changeLimitFilter(draft.limitId));
      dispatch(changeDateFilter({ value: draft.dateId, customValue: draft.customDate }));

      dispatch(
        findStats({
          playername: player.playername,
          pokersiteId: player.pokersiteId,
        }),
      );
    },
    [dispatch, player],
  );

  const resetFilters = useCallback(() => {
    if (!player) {
      return;
    }

    dispatch(dropFilters());
    dispatch(
      findStats({
        playername: player.playername,
        pokersiteId: player.pokersiteId,
      }),
    );
  }, [dispatch, player]);

  const reloadStats = useCallback(() => {
    if (!player) {
      return;
    }

    dispatch(
      findStats({
        playername: player.playername,
        pokersiteId: player.pokersiteId,
      }),
    );
  }, [dispatch, player]);

  return {
    player,
    generalStats,
    graphResults,
    graphLoading,
    monthStats,
    tableSizeStats,
    limitStats,
    showMiningError,
    miningErrorPokersiteId,
    tableSizeFilterValue,
    limitFilterValue,
    dateFilterValue,
    customDateFilterValue,
    filterSummary,
    activeFilterCount,
    applyFilters,
    resetFilters,
    reloadStats,
    tableSizeOptions: TableSizeOptions,
    limitOptions: LimitOptions,
    dateOptions: DateOptions,
  };
}
